// galaxy/glow.js: unresolved starlight + dust absorption, raymarched at reduced
// resolution. Emission and absorption use the face-on maps with exponential
// vertical profiles, integrated exactly in y inside every step, so the thin dust
// layer carves dark lanes without needing hundreds of steps.
import * as THREE from 'three';
import { FullScreenQuad } from '../lib/common.js';
import { GLSL_DISK } from './shade.js';
import { ENC, MAP_RC } from './map.js';

const FRAG = /* glsl */`
precision highp float;
varying vec2 vUv;
${GLSL_DISK}
uniform mat4 uInvProj, uCamWorld;
uniform sampler2D uMapC;
const float MAPRC = ${MAP_RC.toFixed(2)};
// old light + dust, with the inner zoom map (4x finer texels) blended in over the bar and bulge
vec4 mapAz(vec2 xz, float lr, float lrC) {
  vec4 a = mapA(xz, clamp(lr, 0.0, 6.0));
  float m = max(abs(xz.x), abs(xz.y));
  if (m > MAPRC * 0.95) return a;
  vec4 t = textureLod(uMapC, xz * (0.5 / MAPRC) + 0.5, clamp(lrC + log2(MAPR / MAPRC), 0.0, 8.0));
  vec4 c = vec4(t.rgb * t.rgb * ${ENC.col.toFixed(1)}, t.a * t.a * ${ENC.dust.toFixed(1)});
  return mix(a, c, 1.0 - smoothstep(MAPRC * 0.75, MAPRC * 0.95, m));
}
uniform vec3 uCam;
uniform float uPixAng, uBulgeK, uYoungK, uOldK, uDustK, uGain, uHaze, uHazeB, uHazeV, uBarK, uMagF, uSplit, uClump, uHiiG, uTint, uYB;
uniform vec3 uYC, uTintC;
uniform float uCalm, uHp, uHiiD, uYBH, uLcMax; // r8 dive calm (uCalm 0 = r7)
uniform vec2 uFarH, uNearG;                    // r8: far fade of the HII boost (kpc); near-camera range (kpc)
uniform float uNearY;                          // r8: map B dims by uNearY near the camera
uniform float uHcut, uYKnee, uYDes;            // r8: map-B HII cut by footprint; soft knee on the map-B light; young-light desaturation (0 = r7)
uniform vec3 uYW;                              // r8: pale blue-white the young light desaturates to (luminance 1)
uniform float uHiDes; uniform vec2 uHiL;       // r8: bright BLUISH glow desaturates toward white (highlights of a real photo); cream core + pink untouched
uniform vec2 uHF;                              // r8: footprint range (kpc per glow px): full cut below .x, none above .y
uniform int uSteps;
const float HB = 2.2;         // slab half-height that bounds the march (kpc)
const float RB = 17.5;        // cylinder radius (kpc)
vec2 slab(vec3 o, vec3 d) {   // ray interval inside |y| < HB and R < RB
  float t0 = 0.0, t1 = 1e4;
  if (abs(d.y) > 1e-6) { float a = (HB - o.y) / d.y, b = (-HB - o.y) / d.y; t0 = max(t0, min(a, b)); t1 = min(t1, max(a, b)); }
  else if (abs(o.y) > HB) return vec2(1.0, 0.0);
  float A = dot(d.xz, d.xz), B = dot(o.xz, d.xz), C = dot(o.xz, o.xz) - RB * RB;
  float disc = B * B - A * C;
  if (disc < 0.0 || A < 1e-9) return vec2(1.0, 0.0);
  float sq = sqrt(disc);
  t0 = max(t0, (-B - sq) / A); t1 = min(t1, (-B + sq) / A);
  return vec2(t0, t1);
}
float hashj(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
// Bulge, long bar, nuclear disk and nuclear star cluster as Gaussian ellipsoids in the
// bar frame (x along the bar). Their line integrals are analytic (no step noise);
// each is dimmed by the dust transmittance where the ray passes closest to it.
// Shapes: boxy bulge ~1.3 x 0.5 x 0.65 kpc, long bar to ~4.5 kpc (Wegg+2015),
// nuclear disk ~150 pc, NSC ~10 pc. Amplitudes are artistic.
#define NBC 9
const vec3 BC[NBC] = vec3[NBC](vec3(0.0), vec3(0.0), vec3(0.0), vec3(2.0, 0.0, 0.0), vec3(-2.0, 0.0, 0.0), vec3(3.9, 0.0, 0.0), vec3(0.0), vec3(0.0), vec3(-3.9, 0.0, 0.0));
const vec3 BS[NBC] = vec3[NBC](vec3(0.5, 0.22, 0.3), vec3(1.2, 0.42, 0.6), vec3(1.8, 0.7, 0.95), vec3(1.7, 0.18, 0.66), vec3(1.7, 0.18, 0.66), vec3(0.5, 0.12, 0.58), vec3(0.16, 0.05, 0.16), vec3(0.03), vec3(0.5, 0.12, 0.58));
const float BA[NBC] = float[NBC](4.0, 1.3, 0.3, 2.4, 2.4, 0.9, 30.0, 260.0, 0.9);
// SPECTRUM (representative colour): pale cream #F3EADF bulge/bar, white-cream nucleus; luminance as before
const vec3 BK[NBC] = vec3[NBC](vec3(0.85, 0.785, 0.69), vec3(0.76, 0.666, 0.55), vec3(0.73, 0.688, 0.66), vec3(0.78, 0.698, 0.59), vec3(0.78, 0.698, 0.59), vec3(0.79, 0.715, 0.63), vec3(0.93, 0.869, 0.79), vec3(0.97, 0.932, 0.89), vec3(0.79, 0.715, 0.63));
float erfA(float x) { float x2 = x * x; float e = sqrt(1.0 - exp(-x2 * (1.2732395 + 0.147 * x2) / (1.0 + 0.147 * x2))); return x < 0.0 ? -e : e; }
// integral of exp(-0.5 |(p - c) / s|^2) along o + t d for t in [t0, t1]; returns (integral, t closest)
vec2 gaussLine(vec3 o, vec3 d, vec3 c, vec3 s, float t0, float t1) {
  vec3 qo = (o - c) / s, qd = d / s;
  float a = dot(qd, qd), b = dot(qo, qd);
  float ts = -b / a;
  float m2 = max(dot(qo, qo) - b * b / a, 0.0);
  float k = sqrt(0.5 * a);
  return vec2(exp(-0.5 * m2) * 1.2533141 / sqrt(a) * (erfA(k * (t1 - ts)) - erfA(k * (t0 - ts))), ts);
}
void main() {
  vec4 v = uInvProj * vec4(vUv * 2.0 - 1.0, -1.0, 1.0);
  vec3 dir = normalize((uCamWorld * vec4(normalize(v.xyz / v.w), 0.0)).xyz);
  vec3 o = uCam;
  vec2 ti = slab(o, dir);
  vec3 col = vec3(0.0), colY = vec3(0.0);
  vec3 Tr = vec3(1.0);
  if (ti.y > ti.x) {
    // tan-warped steps: dense where the ray meets the mid-plane, sparse far away
    float tc = abs(dir.y) > 1e-5 ? -o.y / dir.y : ti.x;
    tc = clamp(tc, ti.x, ti.y);
    float A = clamp(0.3 / max(abs(dir.y), 1e-3), 0.3, 5.0);
    A = min(A, max(0.02, 0.5 * max(tc, 0.02)));
    float u0 = atan((ti.x - tc) / A), u1 = atan((ti.y - tc) / A);
    float j = hashj(gl_FragCoord.xy);
    // steep rays cross the thin layers quickly: fewer steps (the vertical integrals are exact)
    float N = floor(mix(float(uSteps), max(8.0, float(uSteps) * 0.3), smoothstep(0.35, 0.92, abs(dir.y))));
    float ta = ti.x;
    vec3 ob = toBar(o), db = toBar(dir);
    vec3 bI[NBC]; float bT[NBC];
    for (int k = 0; k < NBC; k++) { vec2 g = gaussLine(ob, db, BC[k], BS[k], ti.x, ti.y); bI[k] = BK[k] * (g.x * BA[k] * uBulgeK * ((k == 0 || k == 1 || k == 6 || k == 7) ? 1.0 : uBarK)); bT[k] = g.y; } // r7: uBarK dims the halo/bar/ansae only
    int done = 0;
    float nextT = 1e9;
    for (int k = 0; k < NBC; k++) nextT = min(nextT, bT[k]);
    // per-pixel shift of the whole step lattice (one extra step): banding -> fine noise
    float js = hashj(gl_FragCoord.yx + 17.0);
    for (int i = 0; i < 97; i++) {
      if (float(i) > N) break;
      float tb = tc + A * tan(mix(u0, u1, clamp((float(i) + 1.0 - js) / N, 0.0, 1.0)));
      tb = min(tb, ti.y);
      float len = tb - ta;
      if (len <= 0.0) { ta = tb; continue; }
      // steep rays sample the maps where the step comes closest to the mid-plane (the weighted point of the
      // thin layers): lanes silhouette at a fixed spot per ray, crisp, not smeared by the step lattice
      float wD = smoothstep(0.05, 0.14, abs(dir.y));
      float tm = mix(mix(ta, tb, 0.3 + 0.4 * j), clamp(tc, ta, tb), wD);
      vec3 pm = o + dir * tm;
      float ya = o.y + dir.y * ta, yb = o.y + dir.y * tb;
      float dy = yb - ya;
      bool flt = abs(dy) < 1e-4;
      float iOld = flt ? vprof(pm.y, H_OLD) * len : (vcdf(yb, H_OLD) - vcdf(ya, H_OLD)) / dy * len;
      float iYng = flt ? vprof(pm.y, H_YNG) * len : (vcdf(yb, H_YNG) - vcdf(ya, H_YNG)) / dy * len;
      float hD = hDust(pm.xz);
      float iDst = flt ? vprof(pm.y, hD) * len : (vcdf(yb, hD) - vcdf(ya, hD)) / dy * len;
      iOld = abs(iOld); iYng = abs(iYng); iDst = abs(iDst);
      // filter the maps over the pixel footprint and (partly) the step, so long in-plane steps don't alias
      float fpx = tm * uPixAng, fst = len * abs(dir.y) * 2.0 + len * 0.08;
      float fD = fpx / sqrt(max(abs(dir.y), 0.05)); // pixel footprint on the plane (mean of its two axes)
      float lr = log2(max(mix(max(fpx, fst), fD, wD), 1e-6) / TEXEL);
      float lod = clamp(lr, 0.0, 6.0);
      vec4 a = mapAz(pm.xz, lr, log2(max(mix(max(fpx, 0.2 * fst), fD, wD), 1e-6) / TEXEL)), b = mapB(pm.xz, clamp(lod + 1.0 * wD, 0.0, 6.0));
      // r7 dive: the inner disk's old light leans blue-white around the cream core and bar (luminance ~kept)
      a.rgb *= mix(vec3(1.0), uTintC, uTint * smoothstep(1.2, 3.5, length(pm.xz)));
      // r8 calm (uHcut = uNearY = 0: r7): map-B HII features magnified past a few glow px read as big soft pink discs, so they fade
      // by their footprint (the crisp, size-capped sprites carry the knots); near the camera map B also dims
      float nk = uNearY * clamp((uNearG.y - tm) / (uNearG.y - uNearG.x), 0.0, 1.0); // linear ramps: cheaper per step than smoothstep
      float hcA = min(uHcut * clamp((uHF.y - tm * uPixAng) / (uHF.y - uHF.x), 0.0, 1.0), 1.0);
      if (hcA + nk > 0.0) {
        float h0 = max(b.r - 0.12 * b.b, 0.0);
        b.rgb = max(b.rgb - (hcA * h0) * vec3(1.0, 0.1, 0.45), 0.0) * (1.0 - nk);
      }
      float mag = mix(smoothstep(0.04, 0.5, tm * uPixAng / TEXEL), 1.0, uMagF); // r7: the dive keeps its young light + HII in the glow (stars are thinned there)
      vec3 bH = vec3(0.0); // r8: the dive's HII light, boosted apart from the young light
      if (uSplit > 0.0) { // r7 dive: unmix map B into young (blue) and HII (pink) light and push them apart: no violet fog
        float hh = max(b.r - 0.12 * b.b, 0.0), yy = max(b.b - 0.45 * hh, 0.0);
        float wk = hh / max(hh + 0.6 * yy, 1e-6);
        // local contrast vs a 2-LOD softer read: clusters and HII knots stand out, the diffuse disk light (the fog) drops
        vec4 bb = mapB(pm.xz, clamp(lod + 1.0 * wD + 2.0, 0.0, 6.0));
        // only on steep rays through a minified map: no flames along the grazing horizon, no blocky near field
        float g0 = wD * smoothstep(0.04, 0.5, tm * uPixAng / TEXEL);
        float lc = pow(clamp(dot(b.rgb, vec3(0.3, 0.4, 0.3)) / max(dot(bb.rgb, vec3(0.3, 0.4, 0.3)), 1e-5), 0.05, mix(3.0, uLcMax, uCalm)), uClump * g0);
        // r8 (uCalm): the glow is too coarse for crisp knots (the sprites carry them): only the HII above the 2-LOD-softer local
        // mean keeps the boost, the diffuse HII stays faint, and the boost fades toward the far horizon (no dotted band)
        float hK = mix(hh, max(hh - uHp * max(bb.r - 0.12 * bb.b, 0.0) * (1.0 - hcA), 0.0), uCalm);
        float gH = g0 * (1.0 - uCalm * clamp((tm - uFarH.x) / (uFarH.y - uFarH.x), 0.0, 1.0));
        vec3 hC = vec3(1.0, 0.07, 0.36) * (0.2 + 0.8 * wk) * (hK * mix(1.0, uHiiG, gH) + (hh - hK) * mix(1.0, uHiiD, uCalm));
        b.rgb = mix(b.rgb, (uYC * yy * (1.0 - 0.8 * wk) + hC * (1.0 - uCalm)) * lc, uSplit);
        bH = hC * lc * (uSplit * uCalm);
      }
      if (uYDes > 0.0) b.rgb = mix(b.rgb, dot(b.rgb, vec3(0.2126, 0.7152, 0.0722)) * uYW, uYDes); // r8: diffuse arm light pale blue-white, not saturated azure (the HII in bH keeps its pink)
      float yr = smoothstep(4.5, 7.0, tm);
      float yk = uYoungK * mix(1.0, uYB, yr); // r7 dive boost: core, arms, horizon; not the near field
      vec3 eY = (b.rgb * yk + bH * (uYoungK * mix(1.0, uYBH, yr))) * iYng * mag; // r8: map-B light (young + HII; HII gets uYBH, not x4), own sum for the knee
      vec3 e = a.rgb * iOld * uOldK;
      float dm = a.a * iDst * uDustK;
      // SPECTRUM (representative colour): near-grey dust with a faint violet-brown edge (no reddening to amber),
      // plus a violet haze (#6B4BD8): dust scattering the local disk light and, near the core, the bulge light
      vec3 dtau = dm * vec3(0.95, 1.0, 0.92);
      if (uHaze + uHazeB > 0.0) { // r8: skipped once the calm dive has cut the haze (dHazeCut): pays for the r8 per-step terms
      float lumL = dot(a.rgb * uOldK + b.rgb * (uYoungK * mag), vec3(0.2126, 0.7152, 0.0722));
      float hz = (1.0 - exp(-dm)) * exp(-1.5 * dm); // glows where dust is thin, dark in the lane cores
      float rh = length(pm.xz) / 1.5;
      // disk-lit haze ~#6B4BD8 (linear light); the bulge-lit haze around the core leans violet
      e += hz * (mix(vec3(0.3, 0.12, 1.0), vec3(0.55, 0.12, 1.0), uHazeV) * (uHaze * lumL) + vec3(0.45, 0.12, 1.0) * (uHazeB * uBulgeK * 2.0 * rh * exp(1.0 - rh)));
      }
      vec3 st = exp(-dtau);
      // emitters share the step with the absorber: (1 - e^-tau) / tau
      vec3 w = mix(vec3(1.0) - 0.5 * dtau, (1.0 - st) / max(dtau, vec3(1e-4)), step(vec3(1e-3), dtau));
      col += Tr * e * w; colY += Tr * eY * w;
      if (nextT < tb) {                   // a bulge component's closest point lies in this step
        nextT = 1e9;
        for (int k = 0; k < NBC; k++) {
          if ((done & (1 << k)) != 0) continue;
          if (bT[k] < tb) { col += Tr * exp(-dtau * clamp((bT[k] - ta) / len, 0.0, 1.0)) * bI[k]; done |= 1 << k; }
          else nextT = min(nextT, bT[k]);
        }
      }
      Tr *= st;
      ta = tb;
      if (ta >= ti.y || max(Tr.r, max(Tr.g, Tr.b)) < 0.003) break;
    }
    for (int k = 0; k < NBC; k++) if ((done & (1 << k)) == 0) col += Tr * bI[k];
  }
  col += colY / (1.0 + uYKnee * uGain * dot(colY, vec3(0.2126, 0.7152, 0.0722))); // r8 knee (uYKnee 0 = r7): bright soft map-B regions compress, dim ones stay
  if (uHiDes > 0.0) {
    float Lc = dot(col, vec3(0.2126, 0.7152, 0.0722));
    float blu = clamp((col.b - col.r) / max(Lc, 1e-6), 0.0, 1.0);
    col = mix(col, vec3(Lc), uHiDes * blu * smoothstep(uHiL.x, uHiL.y, Lc * uGain));
  }
  gl_FragColor = vec4(col * uGain, 1.0);
}`;

export class Glow {
  constructor(maps) {
    this.mat = new THREE.ShaderMaterial({
      uniforms: {
        uMapA: { value: maps.A.texture }, uMapB: { value: maps.B.texture }, uMapC: { value: maps.C ? maps.C.texture : null },
        uInvProj: { value: new THREE.Matrix4() }, uCamWorld: { value: new THREE.Matrix4() },
        uCam: { value: new THREE.Vector3() }, uPixAng: { value: 0.001 },
        uBulgeK: { value: 2.2 }, uYoungK: { value: 1.0 }, uOldK: { value: 1.0 }, uDustK: { value: 1.0 },
        uGain: { value: 1 }, uSteps: { value: 40 }, uHaze: { value: 0.8 }, uHazeB: { value: 0.4 }, uHazeV: { value: 0 }, uBarK: { value: 1 }, uMagF: { value: 0 }, uSplit: { value: 0 }, uClump: { value: 0 }, uHiiG: { value: 1 }, uTint: { value: 0 }, uYB: { value: 1 }, uTintC: { value: new THREE.Vector3(0.66, 1.0, 1.4) },
        uCalm: { value: 0 }, uHp: { value: 1 }, uHiiD: { value: 0.6 }, uYBH: { value: 1.2 }, uLcMax: { value: 2 }, uFarH: { value: new THREE.Vector2(9, 14) }, uNearG: { value: new THREE.Vector2(2.5, 6) }, uNearY: { value: 0 }, uHcut: { value: 0 }, uYKnee: { value: 0 }, uYDes: { value: 0 }, uHiDes: { value: 0 }, uHiL: { value: new THREE.Vector2(0.05, 0.3) }, uYW: { value: new THREE.Vector3(0.748, 0.961, 2.137) }, uHF: { value: new THREE.Vector2(0.004, 0.03) }, // r8
        uYC: { value: new THREE.Vector3(0.12, 0.42, 0.95) },
      },
      vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
      fragmentShader: FRAG,
      depthTest: false, depthWrite: false,
    });
    this.q = new FullScreenQuad(this.mat);
    this.U = this.mat.uniforms;
  }
  setCamera(camera, heightPx) {
    this.U.uInvProj.value.copy(camera.projectionMatrixInverse);
    this.U.uCamWorld.value.copy(camera.matrixWorld);
    this.U.uCam.value.copy(camera.position);
    this.U.uPixAng.value = (2 * Math.tan((camera.fov * Math.PI) / 360)) / Math.max(heightPx, 1);
  }
  render(renderer, target) { renderer.setRenderTarget(target); this.q.render(renderer); }
  dispose() { this.mat.dispose(); this.q.dispose(); }
}
